import React, { useState, useEffect, useCallback } from 'react';
import { forumApi } from "../../api";
import HrnbButton from '../../components/controls/HrnbButton';
import HrnbAlert from '../../components/controls/HrnbAlert';
import HrnbLoader from '../../components/controls/HrnbLoader';
import HrnbModal from '../../components/controls/HrnbModal';
import HrnbForm from '../../components/controls/HrnbForm';
import HrnbInput from '../../components/controls/HrnbInput';
import HrnbFormControl from '../../components/controls/HrnbFormControl';
import HrnbLabel from '../../components/controls/HrnbLabel';
import '../../css/admin/AdminForumTags.css';

export default function AdminForumTags() {
    const [tags, setTags] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [editingTag, setEditingTag] = useState(null);
    const [formData, setFormData] = useState({ name: '', description: '' });

    const fetchTags = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await forumApi.listTags();
            setTags(data || []);
        } catch (err) {
            console.error('[AdminForumTags] Failed to fetch tags:', err.message);
            setError('Не удалось загрузить теги');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchTags();
    }, [fetchTags]);

    const handleOpenCreate = () => {
        setEditingTag(null);
        setFormData({ name: '', description: '' });
        setIsCreateModalOpen(true);
    };

    const handleOpenEdit = (tag) => {
        setEditingTag(tag);
        setFormData({
            name: tag.name || '',
            description: tag.description || '',
        });
        setIsCreateModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsCreateModalOpen(false);
        setEditingTag(null);
        setFormData({ name: '', description: '' });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            // TODO: Добавить API методы для создания/обновления тегов
            // Пока заглушка
            console.log('Submit tag:', formData);
            
            // После успешного создания/обновления обновляем список
            await fetchTags();
            handleCloseModal();
        } catch (err) {
            console.error('[AdminForumTags] Failed to save tag:', err.message);
            setError('Не удалось сохранить тег');
        }
    };

    const handleDelete = async (tagId) => {
        if (!window.confirm('Вы уверены, что хотите удалить этот тег?')) {
            return;
        }

        try {
            // TODO: Добавить API метод для удаления тегов
            // Пока заглушка
            console.log('Delete tag:', tagId);
            
            // После успешного удаления обновляем список
            await fetchTags();
        } catch (err) {
            console.error('[AdminForumTags] Failed to delete tag:', err.message);
            setError('Не удалось удалить тег');
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    if (loading) {
        return <HrnbLoader />;
    }

    return (
        <div className="admin-forum-tags">
            <div className="admin-page-header">
                <h1>Управление тегами форума</h1>
                <HrnbButton variant="primary" onClick={handleOpenCreate}>
                    Создать тег
                </HrnbButton>
            </div>

            {error && (
                <HrnbAlert variant="danger" onClose={() => setError(null)}>
                    {error}
                </HrnbAlert>
            )}

            <div className="admin-tags-list">
                {tags.length === 0 ? (
                    <div className="admin-tags-empty">
                        <p>Тегов пока нет. Создайте первый тег.</p>
                    </div>
                ) : (
                    <table className="admin-tags-table">
                        <thead>
                            <tr>
                                <th>ID</th>
                                <th>Название</th>
                                <th>Slug</th>
                                <th>Постов</th>
                                <th>Описание</th>
                                <th>Действия</th>
                            </tr>
                        </thead>
                        <tbody>
                            {tags.map((tag) => (
                                <tr key={tag.id || tag.slug}>
                                    <td>{tag.id || '-'}</td>
                                    <td>
                                        <span className="tag-name">#{tag.name}</span>
                                    </td>
                                    <td><code>{tag.slug || tag.name?.toLowerCase().replace(/\s+/g, '-')}</code></td>
                                    <td>{tag.postCount || 0}</td>
                                    <td>{tag.description || '-'}</td>
                                    <td>
                                        <div className="admin-actions">
                                            <HrnbButton
                                                variant="outline"
                                                size="sm"
                                                onClick={() => handleOpenEdit(tag)}
                                            >
                                                Редактировать
                                            </HrnbButton>
                                            <HrnbButton
                                                variant="danger"
                                                size="sm"
                                                onClick={() => handleDelete(tag.id || tag.slug)}
                                            >
                                                Удалить
                                            </HrnbButton>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            <HrnbModal
                isOpen={isCreateModalOpen}
                onClose={handleCloseModal}
                title={editingTag ? 'Редактировать тег' : 'Создать тег'}
            >
                <HrnbForm onSubmit={handleSubmit}>
                    <HrnbFormControl>
                        <HrnbLabel htmlFor="name">Название тега</HrnbLabel>
                        <HrnbInput
                            id="name"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            placeholder="Например: javascript"
                            required
                        />
                    </HrnbFormControl>

                    <HrnbFormControl>
                        <HrnbLabel htmlFor="description">Описание</HrnbLabel>
                        <HrnbInput
                            id="description"
                            name="description"
                            value={formData.description}
                            onChange={handleChange}
                            placeholder="Краткое описание тега"
                        />
                    </HrnbFormControl>

                    <div className="modal-actions">
                        <HrnbButton type="button" variant="outline" onClick={handleCloseModal}>
                            Отмена
                        </HrnbButton>
                        <HrnbButton type="submit" variant="primary">
                            {editingTag ? 'Сохранить' : 'Создать'}
                        </HrnbButton>
                    </div>
                </HrnbForm>
            </HrnbModal>
        </div>
    );
}
